import { applySnapshot, flow, Instance, types } from 'mobx-state-tree';
import { generateUUID } from 'utils';
import {
  ACTIVE_STEPS,
  POLL_INTERVAL,
  QR_AUTH_STEP,
  QR_LIFETIME_SECONDS,
  QrAuthStageStatus,
  QrAuthStageStatusType,
  QrCodeAuthStepType,
} from './constants';
import { QrCodeAuth } from 'http-requests';
import { GeneratedQrCode, Statuses } from './models';

export const QrCodeAuthStore = types
  .model('QrCodeAuthStore', {
    requestsStates: types.optional(Statuses, {}),
    qrCodeAuthStep: types.optional(QrCodeAuthStepType, QR_AUTH_STEP.LOADING),
    qrCodeData: types.optional(GeneratedQrCode, {}),
    qrCodeAuthStage: types.optional(
      QrAuthStageStatusType,
      QrAuthStageStatus.MISS
    ),
    pollIntervalId: types.maybeNull(types.frozen<NodeJS.Timeout>()),
    qrCodeTimerValue: types.optional(types.number, 0),
    isPolling: types.optional(types.boolean, false),
    lifetimeIntervalId: types.maybeNull(types.frozen<NodeJS.Timeout>()),
  })
  .actions((self) => {
    let lifetimeIntervalId: NodeJS.Timeout | null = null;

    const clearTimers = () => {
      if (lifetimeIntervalId) {
        clearInterval(lifetimeIntervalId);
        lifetimeIntervalId = null;
      }
    };

    return {
      setQrCodeAuthStep(step: Instance<typeof QrCodeAuthStepType>) {
        self.qrCodeAuthStep = step;
      },
      setQrCodeAuthStage(stage: Instance<typeof QrAuthStageStatusType>) {
        self.qrCodeAuthStage = stage;
      },
      tickLifetimeTimer() {
        self.qrCodeTimerValue -= 1;

        if (self.qrCodeTimerValue <= 0) {
          self.qrCodeAuthStep = QR_AUTH_STEP.EXPIRED;
          clearTimers();
        }
      },
      startLifetimeTimer() {
        clearTimers();
        self.qrCodeTimerValue = QR_LIFETIME_SECONDS;
        lifetimeIntervalId = setInterval(() => {
          this.tickLifetimeTimer();
        }, 1000);
      },
      resetQrAuth() {
        clearTimers();
        self.isPolling = false;
        self.qrCodeAuthStep = QR_AUTH_STEP.LOADING;
        applySnapshot(self, {});
      },

      startPolling: flow(function* () {
        if (self.isPolling) {
          return;
        }

        self.isPolling = true;

        while (ACTIVE_STEPS.has(self.qrCodeAuthStep)) {
          try {
            self.requestsStates.qrAuthStage.loadingStates();

            const response = yield QrCodeAuth.GetQrAuthStage(
              self.qrCodeData.login_session_id
            );

            switch (response.detail) {
              case 'scanned':
                self.qrCodeAuthStep = QR_AUTH_STEP.SCANNED;
                break;
              case 'confirmed':
                self.qrCodeAuthStep = QR_AUTH_STEP.CONFIRMED;
                clearTimers();
                break;
              case 'used':
                self.qrCodeAuthStep = QR_AUTH_STEP.USED;
                clearTimers();
                break;
              case 'cancelled':
                self.qrCodeAuthStep = QR_AUTH_STEP.CANCELLED;
                clearTimers();
                break;
            }

            self.requestsStates.qrAuthStage.successStates();
          } catch (error) {
            console.error('Polling error:', error);
            self.requestsStates.qrAuthStage.errorStates();
          }

          yield new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
        }

        self.isPolling = false;
      }),

      generateQrCode: flow(function* (messenger: string) {
        try {
          self.requestsStates.generateQrCode.loadingStates();
          self.qrCodeAuthStep = QR_AUTH_STEP.LOADING;

          const response = yield QrCodeAuth.GenerateQrCode({
            session_id: generateUUID(),
            messenger,
          });
          self.qrCodeData = GeneratedQrCode.create(response);

          self.requestsStates.generateQrCode.successStates();
        } catch (error) {
          self.requestsStates.generateQrCode.errorStates();

          return Promise.reject(error);
        } finally {
          self.requestsStates.generateQrCode.finallyStates();
        }
      }),
    };
  });
